import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { path, pipe, values, filter, propEq, anyPass } from 'ramda'
import { useHistory } from 'react-router-dom'
import { showVerificationModal } from '@/features/Profile/LivScore/verification-modal'
import config from '@/config'
import isTruthy from '@/utils/is-truthy'

const { BACKEND_SERVER } = config

type Metadata = {
  document_type: string
}

type File = {
  content_type: string
  deleted: string
  document_type: string
  id: string
  metadata: Metadata
  reference_id: string
  reference_type: string
  tag: string
}

type FileMap = Record<string, File>

const filterFinancialFiles = pipe<FileMap[], File[], File[]>(
  values,
  filter(
    anyPass([
      propEq('tag', 'school_verification'),
      propEq('tag', 'employment_verification'),
      propEq('tag', 'income_verification')
    ])
  )
)

const useDocumentTypes = () => {
  const { t } = useTranslation()
  const documentTypes = useMemo<Record<string, string>>(
    () => ({
      t4: t('r.profile.view.t4.text', 'T4'),
      tax_slip: t('r.profile.view.taxslip.text', 'Tax Slips'),
      payslip: t('r.profile.view.payslip.text', 'Pay Slip'),
      bank_statement: t('r.profile.view.bank_statement.text', 'Bank Statement'),
      business_card: t('r.profile.view.business_card.text', 'Business Card'),
      job_offer: t('r.profile.view.job_offer_letter.text', 'Job Offer Letter'),
      student_loan: t('r.profile.view.student_loans.text', 'Student Loans'),
      school_id: t('r.profile.view.school_id.text', 'School ID'),
      school_letter: t('r.profile.view.school_letter.text', 'Enrolment Letter'),
      school_verification: t('r.profile.view.school_verification.text', 'School Verification'),
      income_verification: t('r.profile.view.income_verification.text', 'Income Verification'),
      employment_verification: t(
        'r.profile.view.employment_verification.text',
        'Employment Verification'
      )
    }),
    [t]
  )
  return documentTypes
}

const clickIds: Record<string, string> = {
  school_verification: 'application-trust-score-school-view',
  income_verification: 'application-trust-score-income-view',
  employment_verification: 'application-trust-score-employment-view'
}

const clickIdsRenter: Record<string, string> = {
  school_verification: 'application-trust-score-renter-school-view',
  income_verification: 'application-trust-score-renter-income-view',
  employment_verification: 'application-trust-score-renter-employment-view'
}

const useHandleSecureFiles = (userRole: string) => {
  const { t } = useTranslation()
  const history = useHistory()
  const accountType = useSelector(path(['profile', 'account_type']))
  const personalVerifiedState = useSelector(path(['profile', 'identity_verified_state']))
  const businessVerifiedState = useSelector(
    path(['profile', 'company_info', 'business_licence_verified_state'])
  )
  const landlordRole = useSelector(path(['profile', 'user_role']))
  const isPersonalAccount = () => {
    return userRole === 'landlord' && (accountType === 'personal' || accountType === 'family')
  }
  const isBusinessAccount = () => {
    return userRole === 'landlord' && accountType === 'business'
  }
  const isUserVerified = () => {
    return (
      (isPersonalAccount() && personalVerifiedState === 'verified') ||
      (isBusinessAccount() &&
        businessVerifiedState === 'verified' &&
        personalVerifiedState === 'verified')
    )
  }
  const notUserVerified = () => {
    return userRole === 'landlord' && personalVerifiedState === null
  }

  const isUserPending = () => {
    return userRole === 'landlord' && personalVerifiedState === 'pending'
  }

  const notBusinessVerifiedAdmin = () => {
    return isBusinessAccount() && landlordRole === 'administrator' && businessVerifiedState === null
  }

  const notBusinessVerifiedAgent = () => {
    return isBusinessAccount() && landlordRole === 'agent' && businessVerifiedState === null
  }

  const isBusinessLandlordPending = () => {
    return isBusinessAccount() && businessVerifiedState === 'pending'
  }

  const handleViewSecureFile = (secureFile: File) => {
    if (isUserVerified()) {
      window.open(
        `${BACKEND_SERVER}/secure/${secureFile.tag}/${secureFile.id}/tenant_data.png`,
        '_blank'
      )
    } else if (isUserPending()) {
      showVerificationModal({
        title: t('l.profile.secure_docs.pending.title', 'Unable to proceed'),
        message: t(
          'l.profile.view.secure_docs.pending.warning',
          "For security purposes, only landlords with verified ID's can continue. Since your ID is still pending verification, please wait 1-3 business days while we validate your submitted documents."
        ),
        buttonText: t('b.components.view.got_it.button', 'Got it'),
        history
      })
    } else if (notUserVerified()) {
      showVerificationModal({
        title: t('l.profile.secure_docs.pending.title', 'Unable to proceed'),
        message: t(
          'l.profile.view.secure_docs.not_verified.warning',
          "For security purposes, only landlords with verified ID's can continue. Since your identity is unverified, please submit your ID for verification before continuing."
        ),
        buttonText: t('b.profile.edit.verify_id.text', 'Verify ID'),
        history
      })
    } else if (notBusinessVerifiedAdmin()) {
      showVerificationModal({
        title: t('l.profile.secure_docs.pending.title', 'Unable to proceed'),
        message: t(
          'l.profile.view.secure_docs.not_business_admin_verified.warning',
          "Oops! You can't see tenant's personal documents until you verify your business."
        ),
        buttonText: t('b.settings.view.verify_now.text', 'Verify Now'),
        business: true,
        history
      })
    } else if (notBusinessVerifiedAgent()) {
      showVerificationModal({
        title: t('l.profile.secure_docs.pending.title', 'Unable to proceed'),
        message: t(
          'l.profile.view.secure_docs.not_business_agent_verified.warning',
          "Oops! You can't see tenant's personal documents until you verify your business. Please contact an administrator to verify the business licence."
        ),
        buttonText: t('b.components.success_modal.ok.button', 'OK'),
        history
      })
    } else if (isBusinessLandlordPending()) {
      showVerificationModal({
        title: t('l.profile.secure_docs.pending.title', 'Unable to proceed'),
        message: t(
          'l.profile.view.secure_docs.business_verification_pending.warning',
          "We've received your business licence submission! Hold tight while we validate your submitted document."
        ),
        buttonText: t('b.components.success_modal.ok.button', 'OK'),
        history
      })
    } else {
      window.open(`${BACKEND_SERVER}/secure/${secureFile.tag}/${secureFile.id}/form.png`, '_blank')
    }
  }
  return handleViewSecureFile
}

type AugmentedFile = File & {
  onClick: () => void
  clickId?: string
  docType: string
}

const useFinancialFiles = (files: FileMap, userRole: string): AugmentedFile[] => {
  const documentTypes = useDocumentTypes()
  const handleViewSecureFile = useHandleSecureFiles(userRole)
  const isRenter = userRole === 'tenant'
  const secureFiles = filterFinancialFiles(files)
    .filter(file => !!file)
    .filter(file => !isTruthy(file.deleted))
    .map(file => {
      const docType = documentTypes[file.tag]
      const clickId = !isRenter ? clickIds[file.tag] : clickIdsRenter[file.tag]
      const onClick = () => handleViewSecureFile(file)
      return {
        ...file,
        clickId,
        docType,
        onClick
      }
    })
  return secureFiles
}

export default useFinancialFiles
